import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import AuthShell from "../components/AuthShell";
import { setPageTitle } from "../lib/format";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => setPageTitle("Reset Password"), []);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.resetRequest(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.resetConfirm(token, password);
      toast.success("Password updated — please log in");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell testId="reset-password-page" title="Reset password"
      subtitle={token ? "Choose a new password for your account." : "We'll email you a secure reset link."}>
      {!token ? (
        sent ? (
          <div data-testid="reset-sent" className="space-y-6">
            <p className="text-sm text-zinc-400 leading-relaxed">
              If an account exists for that email, reset instructions will arrive shortly. The link expires in 1 hour and works once.
            </p>
            <Link to="/login" data-testid="reset-back-login" className="btn-secondary w-full text-sm">Back to login</Link>
          </div>
        ) : (
          <form onSubmit={requestReset} className="space-y-5">
            <div>
              <label htmlFor="reset-email" className="label-dark">Account email</label>
              <input id="reset-email" data-testid="reset-email-input" type="email" required autoComplete="email"
                     value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" placeholder="you@example.com" />
            </div>
            <button type="submit" data-testid="reset-request-btn" disabled={busy} className="btn-primary w-full !h-12">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
            </button>
          </form>
        )
      ) : (
        <form onSubmit={confirmReset} className="space-y-5">
          <div>
            <label htmlFor="reset-new-password" className="label-dark">New password</label>
            <input id="reset-new-password" data-testid="reset-new-password-input" type="password" required minLength={8} autoComplete="new-password"
                   value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark" placeholder="At least 8 characters" />
          </div>
          <div>
            <label htmlFor="reset-confirm-password" className="label-dark">Confirm password</label>
            <input id="reset-confirm-password" data-testid="reset-confirm-input" type="password" required minLength={8} autoComplete="new-password"
                   value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-dark" placeholder="Repeat password" />
          </div>
          <button type="submit" data-testid="reset-confirm-btn" disabled={busy} className="btn-primary w-full !h-12">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
          </button>
        </form>
      )}
    </AuthShell>
  );
}
